import React, { useMemo } from "react";

const EliminationBracket = ({ matches, tournament }) => {
  const buildBracketTreeFromMatches = (divisionMatches) => {
    const matchById = {};

    divisionMatches
      .filter((m) => m.round !== "third_place")
      .forEach((m) => {
        matchById[m.id] = { ...m, children: [] };
      });

    const rootMatches = [];

    divisionMatches
      .filter((m) => m.round !== "third_place")
      .forEach((match) => {
        if (match.nextMatchId && matchById[match.nextMatchId]) {
          matchById[match.nextMatchId].children.push(matchById[match.id]);
        } else {
          rootMatches.push(matchById[match.id]);
        }
      });

    const levels = [];

    const traverse = (match, level = 0, reverseChildren = false) => {
      if (!levels[level]) levels[level] = [];
      levels[level].push(match);

      const children = reverseChildren
        ? match.children.slice().reverse()
        : match.children;

      children.forEach((child) => {
        traverse(child, level + 1, false);
      });
    };

    rootMatches.forEach((finalMatch) => {
      traverse(finalMatch, 0, true);
    });

    return levels.reverse();
  };

  const divisions = useMemo(() => {
    const eliminationMatches = matches.filter((m) => m.phase === "elimination");

    const grouped = {};
    eliminationMatches.forEach((m) => {
      const division = m.division || "merged";
      if (!grouped[division]) grouped[division] = [];
      grouped[division].push(m);
    });

    return grouped;
  }, [matches]);

  const hasCrosspoolMatches = useMemo(() =>
    matches?.some((m) => m.phase === "crosspool"), [matches]
  );

  const getDivisionLabel = (divisionKey) => {
    if (!tournament?.makeDivisions && hasCrosspoolMatches) {
      // Use pool terminology for crosspool tournaments
      if (divisionKey === "division1") return "Groupe 1";
      if (divisionKey === "division2") return "Groupe 2";
      if (divisionKey === "final") return "Finale Inter-Groupes";
    }
    // Use division terminology for regular tournaments
    if (divisionKey === "division1") return "Division 1";
    if (divisionKey === "division2") return "Division 2";
    if (divisionKey === "final") return "Finale Inter-Divisions";
    return "Éliminatoire Principal";
  };

  const getRoundDisplayName = (round) => {
    const roundNames = {
      "final": "Finale",
      "semifinal": "Demi-finale",
      "quarterfinal": "Quart de finale",
      "round_of_8": "8e de finale",
      "round_of_16": "16e de finale",
      "round_of_32": "32e de finale",
      "round_of_64": "64e de finale",
      "third_place": "Troisième place",
      "first_round": "Premier tour"
    };
    return roundNames[round] || round?.replaceAll("_", " ") || "Round";
  };

  const getTeamDisplayName = (teamObj, teamNameField) => {
    if (teamObj?.players) {
      return teamObj.players.map((p) => p.name).join(" & ");
    }
    return teamNameField || "BYE";
  };

  const renderMatch = (match) => (
    <div
      key={match.id}
      className="bg-slate-800 text-slate-50 rounded-md p-2 shadow-xl border-2 border-slate-800 w-80 flex text-xs flex-col justify-center h-20 font-bold"
    >
      <div className="truncate">{getTeamDisplayName(match.team1, match.team1Name)}</div>
      <div className="text-xs text-slate-400 my-1">vs</div>
      <div className="truncate">{getTeamDisplayName(match.team2, match.team2Name)}</div>
    </div>
  );

  const renderEmpty = (key) => <div key={key} className="w-80 h-20" />;

  return (
    <div className="flex flex-col gap-12 w-full">
      {Object.keys(divisions)
        .sort((a, b) => {
          if (a === "division1") return -1;
          if (b === "division1") return 1;
          return a.localeCompare(b);
        })
        .map((divisionKey) => {
          const divisionMatches = divisions[divisionKey];
          const rounds = buildBracketTreeFromMatches(divisionMatches);

          if (rounds.length === 0) return null;

          const firstRoundMatches = rounds[0]?.length || 1;
          const totalRows = firstRoundMatches * 2;

          return (
            <div
              key={divisionKey}
              className="flex flex-col items-center bg-slate-900 border-2 border-slate-700 rounded-lg shadow-xl p-2 pb-4"
            >
              <h2 className="text-slate-50 text-2xl font-bold mb-6 capitalize">
                {getDivisionLabel(divisionKey)}
              </h2>

              {/* 🔥 Add full overflow control here */}
              <div className="w-full overflow-x-auto">
                <div className="flex flex-row justify-center gap-8 min-w-max">
                  {rounds.map((matches, roundIdx) => {
                    const elements = Array(totalRows).fill(null);
                    const matchSpacing = Math.pow(2, roundIdx);
                    let matchIndex = 0;

                    for (let i = 0; i < totalRows; i++) {
                      if (i % (matchSpacing * 2) === matchSpacing) {
                        const match = matches[matchIndex];
                        if (match) {
                          elements[i] = renderMatch(match);
                        } else {
                          elements[i] = renderEmpty(`empty-${divisionKey}-${roundIdx}-${i}`);
                        }
                        matchIndex++;
                      } else {
                        elements[i] = renderEmpty(`space-${divisionKey}-${roundIdx}-${i}`);
                      }
                    }

                    return (
                      <div
                        key={roundIdx}
                        className="flex flex-col items-center min-w-[200px]"
                      >
                        <h3 className="text-slate-50 text-lg font-bold mb-4 capitalize">
                          {getRoundDisplayName(matches[0]?.round)}
                        </h3>
                        <div className="flex flex-col">{elements}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default EliminationBracket;
